import PlusOutlined from '@ant-design/icons/PlusOutlined';
import { useQueryClient } from '@tanstack/react-query';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Empty from 'antd/es/empty';
import Modal from 'antd/es/modal';
import Typography from 'antd/es/typography';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ContentContainer from '@components/ContentContainer';
import CreateProjectConversationForm from '@components/forms/CreateProjectConversationForm';
import ConversationsTable from '@components/tables/ConversationsTable';
import { CONVERSATIONS_QUERY_KEY } from '@queries/conversations';
import { useProjectId, useTeamId } from '@utils/router';

const ConversationListPage = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const projectId = useProjectId();
  const teamId = useTeamId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const createConversationModalOpen =
    searchParams.get('createConversationModalOpen') === 'true';

  const setCreateConversationModalOpen = useCallback(
    (open: boolean) => {
      setSearchParams(
        (prev) => {
          if (open) {
            prev.set('createConversationModalOpen', 'true');
          } else {
            prev.delete('createConversationModalOpen');
          }
          return prev;
        },
        {
          replace: true,
        },
      );
    },
    [setSearchParams],
  );

  if (!projectId || !teamId) {
    return (
      <Empty
        description="No team or project selected"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <>
      <Modal
        open={createConversationModalOpen}
        onCancel={() => setCreateConversationModalOpen(false)}
        footer={null}
        width={isMobile ? '100%' : '600px'}
        style={{ top: isMobile ? 0 : 100 }}
        bodyStyle={{
          height: isMobile ? '100vh' : 'auto',
          overflowY: isMobile ? 'auto' : 'visible',
        }}
      >
        <CreateProjectConversationForm
          projectId={projectId}
          afterSubmit={(conversation) => {
            void message.success('Conversation created');
            setCreateConversationModalOpen(false);
            void queryClient.invalidateQueries({
              queryKey: [CONVERSATIONS_QUERY_KEY],
            });
            navigate(
              `/team/${teamId}/project/${projectId}/conversations/${conversation.conversationId}`,
            );
          }}
        />
      </Modal>
      <ContentContainer showFullWidthToggle={false} forceFullWidth>
        <ContentContainer.Header
          title="Conversations"
          subtitle={
            <Typography.Text type="secondary">
              Manage and review all conversations with your AI assistants. Track
              engagement and gain valuable insights.
            </Typography.Text>
          }
          cta={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateConversationModalOpen(true)}
            >
              New Conversation
            </Button>
          }
        />
        <ConversationsTable projectId={projectId} teamId={teamId} />
      </ContentContainer>
    </>
  );
};

export default ConversationListPage;
